<!-- Enhanced component with location selection and localStorage memory -->
<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
    <!-- Overlay -->
    <div class="absolute inset-0" @click="$emit('close')"></div>
    <!-- Modal Container -->
    <div class="relative w-full max-w-2xl p-6 bg-gray-800 rounded-xl shadow-2xl text-white max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-purple-300">NPC ทวงคืนเอาท์โพสต์</h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <p class="mb-2 text-gray-300 text-sm">ระบบอัตโนมัติทำงานทุกวันเวลา 12:00 และ 18:00 น. (เวลาไทย) ครบ 14 สถานที่ ด้วยเลเวล NPC 6–10</p>
      <p class="mb-4 text-gray-300 text-sm">สำหรับการกดเอง เลือกสถานที่และเลเวลที่ต้องการได้ การตั้งค่าจะถูกจำไว้สำหรับครั้งต่อไป</p>

      <!-- Level Range -->
      <div class="flex items-center gap-3 mb-5 p-3 bg-gray-700 rounded-lg border border-gray-600">
        <span class="text-sm text-gray-300 whitespace-nowrap">เลเวล NPC:</span>
        <div class="flex items-center gap-2">
          <label class="text-xs text-gray-400">ต่ำสุด</label>
          <input
            type="number"
            v-model.number="levelMin"
            min="1"
            max="99"
            @change="onLevelChange"
            class="w-16 bg-gray-800 border border-gray-600 text-white text-sm text-center rounded-md px-2 py-1 focus:border-purple-500 focus:outline-none"
          />
        </div>
        <span class="text-gray-500">–</span>
        <div class="flex items-center gap-2">
          <label class="text-xs text-gray-400">สูงสุด</label>
          <input
            type="number"
            v-model.number="levelMax"
            min="1"
            max="99"
            @change="onLevelChange"
            class="w-16 bg-gray-800 border border-gray-600 text-white text-sm text-center rounded-md px-2 py-1 focus:border-purple-500 focus:outline-none"
          />
        </div>
        <span class="ml-2 text-xs text-gray-400">(ค่าเริ่มต้น 6–10)</span>
        <span v-if="levelError" class="ml-2 text-xs text-red-400">{{ levelError }}</span>
      </div>

      <!-- Select All / Deselect All -->
      <div class="flex gap-2 mb-3">
        <button
          @click="selectAll"
          class="px-3 py-1 text-xs bg-purple-700 hover:bg-purple-600 text-white rounded-md transition duration-200"
        >
          เลือกทั้งหมด
        </button>
        <button
          @click="deselectAll"
          class="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-500 text-white rounded-md transition duration-200"
        >
          ยกเลิกทั้งหมด
        </button>
        <span class="ml-auto text-xs text-gray-400 self-center">เลือกแล้ว {{ selectedLocations.length }} / {{ locations.length }} สถานที่</span>
      </div>

      <!-- Location Grid -->
      <div class="grid grid-cols-2 gap-2 mb-6">
        <label
          v-for="location in locations"
          :key="location.name"
          class="flex items-center gap-2 p-3 rounded-lg cursor-pointer border transition duration-200"
          :class="selectedLocations.includes(location.name)
            ? 'bg-purple-900 border-purple-500 text-purple-200'
            : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'"
        >
          <input
            type="checkbox"
            :value="location.name"
            v-model="selectedLocations"
            class="accent-purple-500 w-4 h-4 flex-shrink-0"
            @change="saveToLocalStorage"
          />
          <span class="text-sm leading-tight">{{ location.name }}</span>
          <span class="ml-auto text-xs text-gray-500 flex-shrink-0">{{ location.outposts.length }}</span>
        </label>
      </div>

      <div class="flex justify-end gap-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-300"
        >
          ยกเลิก
        </button>
        <button
          @click="triggerReclaim"
          class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isReclaiming || selectedLocations.length === 0 || !!levelError"
        >
          {{ isReclaiming ? 'กำลังดำเนินการ...' : `เริ่มต้น (${selectedLocations.length} สถานที่)` }}
        </button>
      </div>

      <transition name="fade">
        <div v-if="reclaimMessage" class="mt-4 text-center">
          <p :class="reclaimMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ reclaimMessage }}</p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { projectFunctions } from '@/firebase/config';
import locations from '../../../functions/templates/npcReclaimLocations.json';

const LOCALSTORAGE_KEY = 'npcReclaim_selectedLocations';
const LOCALSTORAGE_LEVEL_KEY = 'npcReclaim_levelRange';

export default {
  emits: ['close'],
  setup() {
    const isReclaiming = ref(false);
    const reclaimMessage = ref('');
    const reclaimMessageType = ref('');
    const selectedLocations = ref([]);
    const levelMin = ref(6);
    const levelMax = ref(10);
    const levelError = ref('');

    onMounted(() => {
      try {
        const saved = localStorage.getItem(LOCALSTORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          const validNames = new Set(locations.map(location => location.name));
          selectedLocations.value = Array.isArray(parsed)
            ? parsed.filter(name => validNames.has(name))
            : locations.map(location => location.name);
        } else {
          selectedLocations.value = locations.map(location => location.name);
        }
      } catch (error) {
        selectedLocations.value = locations.map(location => location.name);
      }

      try {
        const savedLevel = localStorage.getItem(LOCALSTORAGE_LEVEL_KEY);
        if (savedLevel) {
          const parsed = JSON.parse(savedLevel);
          if (typeof parsed.min === 'number') levelMin.value = parsed.min;
          if (typeof parsed.max === 'number') levelMax.value = parsed.max;
        }
      } catch (error) {
        // Use the default range.
      }
      onLevelChange();
    });

    const saveToLocalStorage = () => {
      try {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(selectedLocations.value));
      } catch (error) {
        console.warn('Could not save selected locations:', error);
      }
    };

    const onLevelChange = () => {
      levelError.value = '';
      const min = Number(levelMin.value);
      const max = Number(levelMax.value);
      if (!Number.isInteger(min) || !Number.isInteger(max)) {
        levelError.value = 'กรุณากรอกตัวเลขจำนวนเต็ม';
      } else if (min < 1 || max > 99 || max < 1) {
        levelError.value = 'เลเวลต้องอยู่ระหว่าง 1–99';
      } else if (min > max) {
        levelError.value = 'เลเวลต่ำสุดต้องไม่มากกว่าสูงสุด';
      }
      if (!levelError.value) {
        try {
          localStorage.setItem(LOCALSTORAGE_LEVEL_KEY, JSON.stringify({ min, max }));
        } catch (error) {
          console.warn('Could not save level range:', error);
        }
      }
    };

    const selectAll = () => {
      selectedLocations.value = locations.map(location => location.name);
      saveToLocalStorage();
    };

    const deselectAll = () => {
      selectedLocations.value = [];
      saveToLocalStorage();
    };

    const runNpcReclaim = async () => {
      isReclaiming.value = true;
      reclaimMessage.value = '';
      try {
        const callable = projectFunctions.httpsCallable('npcReclaimOutpostsManual');
        const response = await callable({
          locations: selectedLocations.value,
          levelMin: Number(levelMin.value),
          levelMax: Number(levelMax.value)
        });
        const result = response.data;
        reclaimMessage.value = `สร้าง event ${result.created} สถานที่, ข้าม ${result.skipped} สถานที่${result.failed ? `, ล้มเหลว ${result.failed} สถานที่` : ''}`;
        reclaimMessageType.value = result.failed ? 'error' : 'success';
      } catch (error) {
        console.error('Error in npcReclaimOutpostsManual:', error);
        reclaimMessage.value = `เกิดข้อผิดพลาดในการทวงคืน: ${error.message || 'Unknown error'}`;
        reclaimMessageType.value = 'error';
      } finally {
        isReclaiming.value = false;
      }
    };

    const triggerReclaim = () => {
      onLevelChange();
      if (isReclaiming.value || levelError.value || selectedLocations.value.length === 0) return;
      if (!window.confirm(`ยืนยันการทวงคืน outposts โดย NPC ใน ${selectedLocations.value.length} สถานที่?`)) return;
      if (!window.confirm('ยืนยันอีกครั้ง? การกระทำนี้ไม่สามารถย้อนกลับได้และอาจส่งผลกระทบต่อผู้เล่น')) return;
      runNpcReclaim();
    };

    return {
      locations, selectedLocations, isReclaiming, reclaimMessage,
      reclaimMessageType, triggerReclaim, selectAll, deselectAll,
      saveToLocalStorage, levelMin, levelMax, levelError, onLevelChange
    };
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
